import { request } from './core'
import type { AgentProposal, TranscriptReviewResponse, TranscriptSummary } from './types'

/** Meeting-transcript reconciliation loop. The upload + findings/proposal
 *  mutations are optimistic-locked (If-Match); the question answer/skip use the
 *  shared clarification retry path server-side, so they don't send If-Match. */
export const transcriptReviewApi = {
  getTranscriptReview: (slug: string) =>
    request<TranscriptReviewResponse>(`/api/assessments/${slug}/transcript-review`),

  startTranscriptReview: (slug: string, file: File, revision?: number, name?: string) => {
    const fd = new FormData()
    fd.append('file', file)
    if (name) fd.append('name', name)
    return request<{ status: string; revision: number }>(
      `/api/assessments/${slug}/transcript-review`,
      { method: 'POST', body: fd },
      { ifMatch: revision },
    )
  },

  // --- Saved transcripts (list / view / rename / delete / re-apply) ---

  listTranscripts: (slug: string) =>
    request<{ transcripts: TranscriptSummary[]; revision: number }>(
      `/api/assessments/${slug}/transcripts`,
    ),

  getTranscriptContent: (slug: string, filename: string) =>
    request<{ filename: string; name: string; text: string }>(
      `/api/assessments/${slug}/transcripts/content?filename=${encodeURIComponent(filename)}`,
    ),

  renameTranscript: (slug: string, filename: string, name: string) =>
    request<{ transcript: TranscriptSummary; revision: number }>(
      `/api/assessments/${slug}/transcripts/name`,
      { method: 'PUT', body: JSON.stringify({ filename, name }) },
    ),

  deleteTranscript: (slug: string, filename: string, revision?: number) =>
    request<{ ok: boolean; remaining: number; revision: number }>(
      `/api/assessments/${slug}/transcripts?filename=${encodeURIComponent(filename)}`,
      { method: 'DELETE' },
      { ifMatch: revision },
    ),

  reapplyTranscript: (slug: string, filename: string, revision?: number) =>
    request<{ status: string; revision: number }>(
      `/api/assessments/${slug}/transcripts/reapply`,
      { method: 'POST', body: JSON.stringify({ filename }) },
      { ifMatch: revision },
    ),

  refreshTranscriptReview: (slug: string, revision?: number) =>
    request<{ status: string; revision: number }>(
      `/api/assessments/${slug}/transcript-review/refresh`,
      { method: 'POST' },
      { ifMatch: revision },
    ),

  applyTranscriptFindings: (slug: string, fieldPaths: string[], revision?: number) =>
    request<{ applied: string[]; remaining: number; revision: number }>(
      `/api/assessments/${slug}/transcript-review/findings/apply`,
      { method: 'POST', body: JSON.stringify({ field_paths: fieldPaths }) },
      { ifMatch: revision },
    ),

  dismissTranscriptFindings: (slug: string, fieldPaths: string[], revision?: number) =>
    request<{ dismissed: string[]; remaining: number; revision: number }>(
      `/api/assessments/${slug}/transcript-review/findings/dismiss`,
      { method: 'POST', body: JSON.stringify({ field_paths: fieldPaths }) },
      { ifMatch: revision },
    ),

  acceptTranscriptProposal: (slug: string, index: number, revision?: number) =>
    request<{ ok: boolean; proposal: AgentProposal; revision: number }>(
      `/api/assessments/${slug}/transcript-review/proposals/${index}/accept`,
      { method: 'POST' },
      { ifMatch: revision },
    ),

  rejectTranscriptProposal: (slug: string, index: number, revision?: number) =>
    request<{ ok: boolean; remaining: number; revision: number }>(
      `/api/assessments/${slug}/transcript-review/proposals/${index}/reject`,
      { method: 'POST' },
      { ifMatch: revision },
    ),

  answerTranscriptQuestion: (
    slug: string,
    questionId: string,
    answer: string,
    applyToField: boolean,
  ) =>
    request<{ ok: boolean; revision: number }>(
      `/api/assessments/${slug}/transcript-review/questions/${questionId}/answer`,
      { method: 'POST', body: JSON.stringify({ answer, apply_to_field: applyToField }) },
    ),

  skipTranscriptQuestion: (slug: string, questionId: string) =>
    request<{ ok: boolean; revision: number }>(
      `/api/assessments/${slug}/transcript-review/questions/${questionId}/skip`,
      { method: 'POST' },
    ),
}
